'use client';

import React, { useEffect, useRef } from 'react';

const ALUMNI = [
{
  name: 'Jess Okafor',
  role: 'Offline Editor — BBC Drama',
  before: 'Cutting wedding videos and corporate showreels in a bedroom studio, invisible to broadcasters.',
  after: 'Now offline editing episodic drama for BBC One. First broadcast credit within 6 weeks of graduating.',
  quote: 'I sat down at the Avid on day one and realised I\'d been using a fraction of what the tool could do. By day three I was working like a broadcast editor.',
  discipline: 'Editing',
  tier: 'Master',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5b689fb-1763296744210.png",
  imageAlt: 'Jess Okafor, a young Black woman with natural hair, smiling confidently in a studio environment',
  stat: '6 weeks',
  statLabel: 'to first broadcast credit'
},
{
  name: 'Ravi Menon',
  role: 'Senior Colourist — Framestore',
  before: 'Junior designer at a mid-size London agency, doing motion graphics with no formal colour training.',
  after: 'Senior colourist at Framestore, grading feature-length projects with a DaVinci Resolve bay to himself.',
  quote: 'The colour bay at Upskill is the exact rig Framestore uses. When I walked into my interview I already knew the room.',
  discipline: 'Colour',
  tier: 'Intermediate',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a38455a-1763294317360.png",
  imageAlt: 'Ravi Menon, a South Asian man in his late twenties, wearing a dark shirt in a professional setting',
  stat: '3× salary',
  statLabel: 'uplift in 18 months'
},
{
  name: 'Caitlin Byrne',
  role: 'Production Founder — Bright Signal',
  before: 'Running a small Dublin production company, onboarding new hires who weren\'t hitting broadcast standards.',
  after: 'Enrolled her entire new cohort of four editors. All four passed their first Channel 4 compliance review.',
  quote: 'I stopped losing two weeks per hire to remedial training. Now they arrive knowing exactly what broadcast standard means — because they\'ve worked to it.',
  discipline: 'Directing',
  tier: 'Master',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e99f423b-1772066258265.png",
  imageAlt: 'Caitlin Byrne, a white Irish woman in her thirties with auburn hair, in a production office',
  stat: '4/4',
  statLabel: 'hires passed compliance'
}];


export default function AlumniSection() {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.15 }
    );

    cardRefs.current.forEach((card, i) => {
      if (card) {
        card.classList.add('reveal-hidden');
        card.style.transitionDelay = `${i * 120}ms`;
        observer.observe(card);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="alumni"
      className="py-24 px-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0A1828 0%, #0B1A2E 100%)' }}>
      
      {/* Atmospheric accent */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '0',
          right: '-10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(ellipse, rgba(58,74,92,0.15) 0%, transparent 70%)'
        }} />
      

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div className="mb-16">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 text-xs font-semibold tracking-widest uppercase"
            style={{ background: 'rgba(244,246,248,0.06)', border: '1px solid rgba(244,246,248,0.1)', color: 'rgba(244,246,248,0.55)' }}>
            
            Alumni Transformations
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2
              className="font-display font-light leading-none tracking-tight"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', color: '#F4F6F8' }}>
              
              The gap closes{' '}
              <em style={{ color: 'rgba(244,246,248,0.4)' }}>faster</em>
              <br />
              than you think.
            </h2>
            <p
              className="text-sm font-light max-w-xs"
              style={{ color: 'rgba(244,246,248,0.45)' }}>
              
              Real people. Real briefs. Real broadcast environments. The transformation isn't gradual — it's the first time you sit at the right workstation.
            </p>
          </div>
        </div>

        {/* Alumni cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {ALUMNI.map((alum, i) =>
          <div
            key={alum.name}
            ref={(el) => {cardRefs.current[i] = el;}}
            className="alumni-card rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(58,74,92,0.25) 0%, rgba(15,34,64,0.4) 100%)',
              border: '1px solid rgba(244,246,248,0.08)'
            }}>
            
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                src={alum.image}
                alt={alum.imageAlt}
                className="w-full h-full object-cover transition-transform duration-700"
                style={{ filter: 'grayscale(20%)' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'} />
              
                {/* Gradient overlay */}
                <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(11,26,46,0.9) 0%, transparent 50%)' }} />
              
                {/* Tier badge */}
                <div
                className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{
                  background: alum.tier === 'Master' ? 'rgba(0,230,118,0.15)' : 'rgba(244,246,248,0.1)',
                  border: alum.tier === 'Master' ? '1px solid rgba(0,230,118,0.3)' : '1px solid rgba(244,246,248,0.15)',
                  color: alum.tier === 'Master' ? '#00E676' : '#F4F6F8'
                }}>
                
                  {alum.tier} · {alum.discipline}
                </div>
                {/* Stat overlay */}
                <div className="absolute bottom-3 right-3 text-right">
                  <div
                  className="font-display font-light text-2xl leading-none"
                  style={{ color: '#00E676' }}>
                  
                    {alum.stat}
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(244,246,248,0.5)' }}>
                    {alum.statLabel}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Before/After narrative */}
                <div
                className="text-xs font-semibold tracking-widest uppercase mb-2"
                style={{ color: 'rgba(244,246,248,0.3)' }}>
                
                  Before
                </div>
                <p className="text-sm mb-4" style={{ color: 'rgba(244,246,248,0.5)' }}>
                  {alum.before}
                </p>
                <div
                className="text-xs font-semibold tracking-widest uppercase mb-2"
                style={{ color: '#00E676', opacity: 0.7 }}>
                
                  After
                </div>
                <p className="text-sm mb-5" style={{ color: 'rgba(244,246,248,0.75)' }}>
                  {alum.after}
                </p>

                {/* Quote */}
                <blockquote
                className="text-sm italic leading-relaxed mb-5 pl-3"
                style={{
                  color: 'rgba(244,246,248,0.6)',
                  borderLeft: '2px solid rgba(0,230,118,0.4)'
                }}>
                
                  "{alum.quote}"
                </blockquote>

                {/* Attribution */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#F4F6F8' }}>
                      {alum.name}
                    </div>
                    <div className="text-xs" style={{ color: 'rgba(244,246,248,0.4)' }}>
                      {alum.role}
                    </div>
                  </div>
                  <a
                  href="#signup"
                  className="text-xs font-semibold transition-colors duration-200"
                  style={{ color: '#00E676' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#F4F6F8'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#00E676'}>
                  
                    Start Free →
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA after alumni */}
        <div className="text-center">
          <a
            href="#signup"
            className="btn-magnetic pulse-green inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-sm font-semibold"
            style={{ background: '#00E676', color: '#0B1A2E' }}>
            
            <span className="blink w-2 h-2 rounded-full" style={{ background: '#0B1A2E', opacity: 0.7 }} />
            Start Your Free Module — No Card Required
          </a>
        </div>
      </div>
    </section>);

}